import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EntityResponseDto, StatusResponse, UserPermissionsService } from 'rome-library';
import { forkJoin, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ExternalUserResetPasswordDto } from '../models/external-user-reset-password-dto.model';
import { ForgottenPasswordResetRequestDto } from '../models/forgotten-password-reset-request-dto.model';
import { User } from '../models/user.model';
import { ValidateOtpRequest } from '../models/otp.model';
import { ContactNumberResetOtp } from '../models/contactNumberResetOtp.model';
import { ExternalUserUpdateProposedContactNumberDto } from '../../interfaceTypes/sharedFeatures/user-profile-shared-features/models/external-user-update-contact-number-dto.model';

@Injectable()
export abstract class UserService {
  private user: User;

  public displayFeatureWindow: boolean;

  public displayNotificationDialog = true;

  constructor(
    protected readonly httpClient: HttpClient,
    private readonly userPermissionsService: UserPermissionsService
  ) {}

  public abstract getUserInfoEndpoint(): string;
  public abstract getSendResetForgottenPasswordRequestEndpoint(): string;
  public abstract getSecurityChallengeForPasswordResetRequestEndpoint(): string;
  public abstract getResetForgottenPasswordRequestEndpoint(): string;
  public abstract getCheckSecurityQuestionAnswerForPasswordResetRequestEndpoint(): string;
  public abstract getResetPasswordEndpoint(): string;

  public getUser(): User {
    return this.user;
  }

  public setUser(user: User): void {
    this.user = user;
  }

  public setUserData(loginData, password): Observable<[any, boolean, string]> {
    this.setUserLoginRelatedData(loginData);
    this.displayNotificationDialog = true;

    const userDataObservable = this.getUserInfo();

    return forkJoin([userDataObservable, this.getCookiesResponse(), this.getCurrentApplicationVersion()]);
  }

  public getUserInfo(): Observable<any> {
    return this.httpClient.get(this.getUserInfoEndpoint()).pipe(
      tap((data) => {
        const user = new User().deserialize(data);
        this.setUser(user);
        this.user.IsAuthenticated = true;
        this.userPermissionsService.setPermissions(this.user.Permissions);
      })
    );
  }

  private setUserLoginRelatedData(data): void {
    this.displayFeatureWindow = this.isTrue(data.displayFeatureWindow);
  }

  private isTrue(value: string): boolean {
    return value === 'True';
  }

  private getCurrentApplicationVersion(): Observable<string> {
    return this.httpClient.get<any>('/Configuration/currentApplicationVersion').pipe(
      map((data) => {
        return data.currentApplicationConfiguration;
      })
    );
  }

  public sendResetForgottenPasswordRequest(emailAddress: string): Observable<any> {
    return this.httpClient
      .post<any>(this.getSendResetForgottenPasswordRequestEndpoint(), JSON.stringify(emailAddress), {
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map((data) => {
          return JSON.parse(data.Message);
        })
      );
  }

  public getSecurityChallengeForPasswordReset(guid: string): Observable<any> {
    const endpoint = this.getSecurityChallengeForPasswordResetRequestEndpoint();

    return this.httpClient.get<any>(endpoint, {
      params: {
        forgottenPasswordResetRequestGuid: guid
      }
    });
  }

  public resetForgottenPasswordRequest(
    forgottenPasswordResetRequestDto: ForgottenPasswordResetRequestDto
  ): Observable<EntityResponseDto<string>> {
    return this.httpClient
      .post<{ EmailAddress: string }>(this.getResetForgottenPasswordRequestEndpoint(), forgottenPasswordResetRequestDto)
      .pipe(
        map((response) => {
          const entityResponse = new EntityResponseDto<string>().deserialize(response);
          entityResponse.Entity = response.EmailAddress;

          return entityResponse;
        })
      );
  }

  public checkSecurityQuestionAnswerForPasswordReset(
    forgottenPasswordResetRequestDto: ForgottenPasswordResetRequestDto
  ): Observable<boolean> {
    return this.httpClient.post<boolean>(
      this.getCheckSecurityQuestionAnswerForPasswordResetRequestEndpoint(),
      forgottenPasswordResetRequestDto
    );
  }

  public resetPassword(resetPasswordDto: ExternalUserResetPasswordDto): Observable<StatusResponse> {
    return this.httpClient.post<StatusResponse>(this.getResetPasswordEndpoint(), resetPasswordDto);
  }

  public acceptPrivacyNotice(): Observable<any> {
    return this.httpClient.put('/ExternalAgents/AcceptPrivacyNotice', undefined);
  }

  public acceptCookies(): Observable<any> {
    return of();
  }

  public getCookiesResponse(): Observable<boolean> {
    return of(true);
  }

  public sendOtpCodeForPasswordResetRequest(
    requestGuid: string
  ): Observable<{ HasExceededMaximumOtpSendLimit: boolean }> {
    return this.httpClient.post<{ HasExceededMaximumOtpSendLimit: boolean }>(
      '/DirectCustomer/sendOtpCodeForPasswordResetRequest',
      { requestGuid }
    );
  }

  public validateOtpCode(validateOtpRequest: ValidateOtpRequest): Observable<any> {
    return this.httpClient.post<boolean>('/DirectCustomer/validateOtpCodeForPasswordResetRequest', validateOtpRequest);
  }

  public sendOtpCodeForContactNumberResetRequest(
    updateProposedContactNumberDto: ExternalUserUpdateProposedContactNumberDto
  ): Observable<{ HasExceededMaximumOtpSendLimit: boolean }> {
    return this.httpClient.post<{ HasExceededMaximumOtpSendLimit: boolean }>(
      '/DirectCustomer/sendOtpCodeForContactNumberResetRequest',
      updateProposedContactNumberDto
    );
  }

  public validateContactNumberResetOtpCode(validateOtpRequest: ContactNumberResetOtp): Observable<any> {
    return this.httpClient.post<boolean>(
      '/DirectCustomer/validateOtpCodeForContactNumberResetRequest',
      validateOtpRequest
    );
  }
}
